import { DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents } from '@theme/events';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';
import { debounce } from '@theme/utilities';

/**
 * Loads recently viewed product strip into the cart drawer (same markup/classes as Discover more).
 * Refills after header/cart morphs (discount flow fires discount:update then async renderSection).
 */
class CartDrawerRecentlyViewed extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {MutationObserver | null} */
  #hostObserver = null;

  /** @type {boolean} */
  #loadInFlight = false;

  #debouncedReload = debounce(() => {
    void this.#load();
  }, 120);

  connectedCallback() {
    const drawer = this.closest('cart-drawer-component');
    if (!drawer) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    drawer.addEventListener(DialogOpenEvent.eventName, this.#onDrawerOpen, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartOrDiscountUpdate, { signal });
    document.addEventListener(ThemeEvents.discountUpdate, this.#onCartOrDiscountUpdate, { signal });

    const dialog = drawer.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) {
      this.#dialogObserver = new MutationObserver(() => {
        if (dialog.open) queueMicrotask(() => void this.#load());
      });
      this.#dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
      if (dialog.open) queueMicrotask(() => void this.#load());
    }

    // Morph (discount + async renderSection) can wipe client HTML without changing dialog `open`.
    this.#hostObserver = new MutationObserver(() => {
      if (!this.isConnected) return;
      const d = this.closest('cart-drawer-component')?.querySelector('dialog');
      if (!(d instanceof HTMLDialogElement) || !d.open) return;
      if (RecentlyViewed.getProducts().length === 0) return;
      if (this.querySelector('#cart-drawer-recently-viewed-root')) return;
      this.#debouncedReload();
    });
    this.#hostObserver.observe(this, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.#dialogObserver?.disconnect();
    this.#dialogObserver = null;
    this.#hostObserver?.disconnect();
    this.#hostObserver = null;
    this.#abort?.abort();
    this.#abort = null;
  }

  #onDrawerOpen = () => {
    queueMicrotask(() => void this.#load());
  };

  #onCartOrDiscountUpdate = () => {
    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement && dialog.open) {
      this.#debouncedReload();
    }
  };

  #load = async () => {
    if (this.#loadInFlight) return;
    if (!this.isConnected) return;

    const drawer = this.closest('cart-drawer-component');
    const dialog = drawer?.querySelector('dialog');
    if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const ids = RecentlyViewed.getProducts();
    if (ids.length === 0) {
      this.hidden = true;
      this.innerHTML = '';
      return;
    }

    this.#loadInFlight = true;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', ids.map((/** @type {string} */ id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);

      if (!this.isConnected) return;
      const d = this.closest('cart-drawer-component')?.querySelector('dialog');
      if (!(d instanceof HTMLDialogElement) || !d.open) return;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const root = doc.querySelector('#cart-drawer-recently-viewed-root');

      if (root && root.innerHTML.trim().length > 0) {
        this.innerHTML = root.outerHTML;
        this.hidden = false;
      } else {
        this.hidden = true;
        this.innerHTML = '';
      }
    } catch (e) {
      console.error('Cart drawer recently viewed:', e);
      if (!Shopify.designMode) {
        this.hidden = true;
        this.innerHTML = '';
      }
    } finally {
      this.#loadInFlight = false;
    }
  };
}

if (!customElements.get('cart-drawer-recently-viewed')) {
  customElements.define('cart-drawer-recently-viewed', CartDrawerRecentlyViewed);
}
